<script setup lang="ts">
// Static illustration of a full mychar page for the landing hero, mirroring
// the real page structure: header card, links, games, embeds, and the
// floating audio player and views counter widgets.
</script>

<template>
  <div
    aria-hidden="true"
    class="relative hidden lg:block select-none pointer-events-none"
  >
    <!-- Soft backdrop tint -->
    <div class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-105 rounded-full bg-primary-500/10 blur-3xl" />

    <!-- Page mock -->
    <div class="relative overflow-hidden rounded-2xl border border-(--mock-card-border) p-4 shadow-2xl shadow-black/30 transform-[perspective(1400px)_rotateY(-6deg)_rotateX(2deg)_rotate(0.5deg)] [background:var(--mock-page-bg)]">
      <!-- Background particles -->
      <span class="absolute left-[12%] top-[18%] size-1 rounded-full bg-primary-400/50" />
      <span class="absolute left-[85%] top-[10%] size-1.5 rounded-full bg-fuchsia-400/30" />
      <span class="absolute left-[70%] top-[55%] size-1 rounded-full bg-primary-300/40" />
      <span class="absolute left-[8%] top-[70%] size-1.5 rounded-full bg-fuchsia-300/25" />
      <span class="absolute left-[45%] top-[88%] size-1 rounded-full bg-primary-400/35" />
      <span class="absolute left-[92%] top-[78%] size-1 rounded-full bg-primary-300/30" />

      <!-- Header card -->
      <div class="relative flex items-center gap-3 rounded-xl border border-(--mock-card-border) bg-(--mock-card-bg) p-3.5">
        <div class="flex size-11 shrink-0 items-center justify-center rounded-full bg-linear-to-br from-primary-400 to-fuchsia-500 text-base font-bold text-white">
          A
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <p class="text-sm font-semibold text-(--mock-text-strong)">
              Ava
            </p>
            <span class="flex items-center gap-1">
              <UIcon
                name="i-lucide-crown"
                class="size-3.5 text-amber-400"
              />
              <UIcon
                name="i-lucide-gem"
                class="size-3.5 text-fuchsia-400"
              />
              <UIcon
                name="i-lucide-rocket"
                class="size-3.5 text-sky-400"
              />
              <UIcon
                name="i-lucide-heart"
                class="size-3.5 text-rose-400"
              />
            </span>
          </div>
          <p class="text-[11px] text-(--mock-text-dim)">
            she/her · Berlin · mychar.gg/ava
          </p>
        </div>
      </div>

      <div class="relative mt-2.5 grid grid-cols-5 gap-2.5">
        <!-- Left column: games + setup -->
        <div class="col-span-2 flex flex-col gap-2.5">
          <div class="rounded-xl border border-(--mock-card-border) bg-(--mock-card-bg) p-3">
            <MockSectionTitle
              label="Games I play"
              compact
            />
            <div class="mt-2.5 grid grid-cols-2 gap-1.5">
              <div class="flex flex-col items-center gap-1 rounded-lg border border-(--mock-card-border) bg-(--mock-tile-bg) p-2">
                <UIcon
                  name="i-lucide-gamepad-2"
                  class="size-5 text-fuchsia-400"
                />
                <p class="text-[8px] leading-tight text-(--mock-text-muted)">
                  Valorant
                </p>
                <span class="flex max-w-full items-center gap-0.5 truncate rounded-full border border-(--mock-card-border) bg-(--mock-tile-bg) px-1.5 py-0.5 text-[7px] text-(--mock-text-muted)">
                  <UIcon
                    name="i-lucide-copy"
                    class="size-1.5 opacity-60"
                  />
                  ava#eu
                </span>
              </div>
              <div class="flex flex-col items-center gap-1 rounded-lg border border-(--mock-card-border) bg-(--mock-tile-bg) p-2">
                <UIcon
                  name="i-lucide-gamepad-2"
                  class="size-5 text-emerald-400"
                />
                <p class="text-[8px] leading-tight text-(--mock-text-muted)">
                  Minecraft
                </p>
                <span class="flex max-w-full items-center gap-0.5 truncate rounded-full border border-(--mock-card-border) bg-(--mock-tile-bg) px-1.5 py-0.5 text-[7px] text-(--mock-text-muted)">
                  <UIcon
                    name="i-lucide-copy"
                    class="size-1.5 opacity-60"
                  />
                  ava_
                </span>
              </div>
            </div>
          </div>

          <div class="rounded-xl border border-(--mock-card-border) bg-(--mock-card-bg) p-3">
            <MockSectionTitle
              label="My setup"
              compact
            />
            <div class="mt-2.5 flex items-center gap-1.5">
              <UIcon
                name="i-lucide-cpu"
                class="size-2.5 text-(--mock-text-dim)"
              />
              <span class="text-[8px] uppercase tracking-wider text-(--mock-text-dim)">PC / Specs</span>
            </div>
            <div class="mt-1 space-y-1">
              <p class="flex items-center gap-1 text-[10px] text-(--mock-text) underline decoration-dotted decoration-(--mock-dot) underline-offset-2">
                RTX 4080 Super
                <UIcon
                  name="i-lucide-external-link"
                  class="size-2.5 opacity-40"
                />
              </p>
              <p class="flex items-center gap-1 text-[10px] text-(--mock-text) underline decoration-dotted decoration-(--mock-dot) underline-offset-2">
                Custom 65% keyboard
                <UIcon
                  name="i-lucide-external-link"
                  class="size-2.5 opacity-40"
                />
              </p>
            </div>
          </div>
        </div>

        <!-- Right column: links + discord -->
        <div class="col-span-3 flex flex-col gap-2.5">
          <div class="rounded-xl border border-(--mock-card-border) bg-(--mock-card-bg) p-3">
            <MockSectionTitle
              label="My links"
              compact
            />
            <div class="mt-2.5 flex justify-center gap-1.5">
              <span class="flex size-8 items-center justify-center rounded-lg border border-(--mock-card-border) bg-(--mock-tile-bg)">
                <UIcon
                  name="i-simple-icons-youtube"
                  class="size-3.5 text-red-400"
                />
              </span>
              <span class="flex size-8 items-center justify-center rounded-lg border border-(--mock-card-border) bg-(--mock-tile-bg)">
                <UIcon
                  name="i-simple-icons-tiktok"
                  class="size-3.5 text-(--mock-text)"
                />
              </span>
              <span class="flex size-8 items-center justify-center rounded-lg border border-(--mock-card-border) bg-(--mock-tile-bg)">
                <UIcon
                  name="i-simple-icons-instagram"
                  class="size-3.5 text-pink-400"
                />
              </span>
              <span class="flex size-8 items-center justify-center rounded-lg border border-(--mock-card-border) bg-(--mock-tile-bg)">
                <UIcon
                  name="i-simple-icons-x"
                  class="size-3.5 text-(--mock-text)"
                />
              </span>
            </div>
            <div class="mt-2 flex flex-col gap-1.5">
              <div class="flex items-center gap-2 rounded-lg border border-(--mock-card-border) bg-(--mock-card-bg) px-2.5 py-2">
                <UIcon
                  name="i-simple-icons-youtube"
                  class="size-3.5 shrink-0 text-red-400"
                />
                <span class="truncate text-[11px] font-medium text-(--mock-text)">New video: studio tour</span>
                <UIcon
                  name="i-lucide-arrow-up-right"
                  class="ml-auto size-3 shrink-0 text-(--mock-text-dim)"
                />
              </div>
              <div class="flex items-center gap-2 rounded-lg border border-rose-400/25 bg-rose-500/8 px-2.5 py-2">
                <UIcon
                  name="i-lucide-heart"
                  class="size-3.5 shrink-0 text-rose-400"
                />
                <span class="truncate text-[11px] font-medium text-(--mock-text)">Support my work</span>
                <UIcon
                  name="i-lucide-arrow-up-right"
                  class="ml-auto size-3 shrink-0 text-(--mock-text-dim)"
                />
              </div>
            </div>
          </div>

          <!-- Discord server embed -->
          <div class="overflow-hidden rounded-xl border border-(--mock-card-border) bg-(--mock-card-bg)">
            <div class="h-1 w-full bg-[#5865F2]" />
            <div class="p-2.5">
              <div class="flex items-center gap-2.5">
                <div class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#5865F2]/20">
                  <UIcon
                    name="i-simple-icons-discord"
                    class="size-4 text-[#5865F2]"
                  />
                </div>
                <p class="truncate text-xs font-semibold text-(--mock-text-strong)">
                  Ava's hangout
                </p>
              </div>
              <div class="mt-2 flex items-center justify-between">
                <div class="flex items-center gap-2.5">
                  <span class="flex items-center gap-1 text-[10px] text-(--mock-text-muted)">
                    <span class="size-1.5 rounded-full bg-green-500" />
                    43 Online
                  </span>
                  <span class="flex items-center gap-1 text-[10px] text-(--mock-text-muted)">
                    <span class="size-1.5 rounded-full bg-(--mock-dot)" />
                    312 Members
                  </span>
                </div>
                <span class="flex items-center gap-1 text-[10px] font-medium text-[#5865F2]">
                  Join
                  <UIcon
                    name="i-lucide-external-link"
                    class="size-2.5"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Floating widgets row: audio player + views counter -->
      <div class="relative mt-2.5 flex items-center justify-between gap-2.5">
        <div class="flex min-w-0 items-center gap-2.5 rounded-xl border border-(--mock-card-border) bg-(--mock-card-bg) px-3 py-2">
          <span class="flex size-6 shrink-0 items-center justify-center rounded-full border border-primary-400/30 bg-primary-500/20">
            <UIcon
              name="i-lucide-pause"
              class="size-3 text-primary-500"
            />
          </span>
          <div class="flex h-3 shrink-0 items-end gap-0.5 text-primary-400">
            <span class="wave-dot" />
            <span
              class="wave-dot"
              style="animation-delay: 0.15s"
            />
            <span
              class="wave-dot"
              style="animation-delay: 0.3s"
            />
            <span
              class="wave-dot"
              style="animation-delay: 0.45s"
            />
          </div>
          <div class="min-w-0">
            <p class="truncate text-[10px] text-(--mock-text-muted)">
              night drive
            </p>
            <div class="mt-1 h-0.5 w-24 rounded-full bg-(--mock-track)">
              <div class="h-full w-2/5 rounded-full bg-primary-400/60" />
            </div>
          </div>
        </div>

        <div class="flex shrink-0 items-center gap-1.5 rounded-full border border-primary-400/20 bg-primary-500/10 px-3 py-1.5">
          <UIcon
            name="i-lucide-eye"
            class="size-3 text-primary-500"
          />
          <span class="text-[10px] font-bold tabular-nums text-(--mock-text-strong)">1,284</span>
          <span class="text-[9px] font-medium text-(--mock-text-muted)">views</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.wave-dot {
  width: 3px;
  height: 3px;
  border-radius: 9999px;
  background: currentcolor;
  animation: wave-bounce 1s ease-in-out infinite;
}

@keyframes wave-bounce {
  0%, 100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-5px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .wave-dot {
    animation: none;
  }
}
</style>
